import type { Venue } from "@/types/api/contracts/venue";
import { cn } from "@/utils/css";
import { useEffect, useState } from "react";

/**
 * Interface representing the props for the sidebar organisation block.
 * @interface ISideBarProfile
 * @property {Venue} [organisation]: The organisation whose name is shown.
 * @property {boolean} collapseSidebar: Whether the sidebar is collapsed (icon-only).
 */
export interface ISideBarProfile {
  organisation?: Venue;
  collapseSidebar: boolean;
}

const SideBarProfile: React.FC<ISideBarProfile> = ({
  organisation,
  collapseSidebar,
}) => {
  const organisationInitials = (organisation?.name ?? "")
    .split(" ")
    .slice(0, 2)
    .map((word) => word.charAt(0))
    .join("")
    .toUpperCase();

  // On desktop the banner label settles to "ORG" while collapsed. The full
  // word is held through the width slide (and truncates smoothly as it
  // narrows), swapping to "ORG" only once the sidebar has finished collapsing;
  // expanding shows the full word at once so it grows back from truncated.
  const [desktopShowFullLabel, setDesktopShowFullLabel] =
    useState(!collapseSidebar);

  useEffect(() => {
    if (!collapseSidebar) {
      setDesktopShowFullLabel(true);
      return;
    }
    const timer = setTimeout(() => setDesktopShowFullLabel(false), 300);
    return () => clearTimeout(timer);
  }, [collapseSidebar]);

  return (
    <div className="shrink-0 bg-gray-800">
      <div
        className={cn(
          "flex items-center bg-dines-green-gradient px-8 py-3",
          collapseSidebar && "md:justify-center md:px-2",
        )}
      >
        <p
          className={cn(
            "mt-1 min-w-0 flex-1 truncate font-bold text-dines-purple-darkest text-sm leading-none tracking-wide",
            collapseSidebar && "md:text-center",
          )}
        >
          <span className="md:hidden">ORGANISATION</span>
          <span className="hidden md:inline">
            {desktopShowFullLabel ? "ORGANISATION" : "ORG"}
          </span>
        </p>
      </div>
      <div className="relative h-20 px-8 py-4">
        <p
          className={cn(
            "line-clamp-2 overflow-hidden break-words font-bold text-base text-white",
            collapseSidebar && "md:hidden",
          )}
        >
          {organisation?.name}
        </p>
        {collapseSidebar && (
          <div className="pointer-events-none absolute inset-0 hidden items-center justify-center md:flex">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-white/10 bg-dines-purple-darkest font-bold text-base text-white uppercase">
              {organisationInitials}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SideBarProfile;
